import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Chip,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useApplicationUser, UserDto } from "../../../hooks/useApplicationUser";
import { useAuth } from "../../../contexts/AuthContext";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import { useTenantHeader } from "../../../hooks/useTenantHeader";
import ApplicationUserModal from "components/Modals/ApplicationUserModal";
import EnhancedTable from "../../../components/common/EnhancedTable";
import CommonModal from "../../../components/common/CommonModal";

/**
 * ApplicationUserManagement Component
 * 
 * Manages application users - allows school admins to create users
 * from submitted applications or manually create new users.
 */
export default function ApplicationUserManagement() {
  const { t } = useTranslation();
  const { user, isSchoolAdmin } = useAuth();
  const { tenantLoading } = useTenantHeader();

  // UI state
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserDto | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // API hooks
  const { 
    applicationUsers, 
    isLoading, 
    isFetching,
    createUser, 
    isCreating,
    updateUser,
    isUpdating,
    deleteUser,
    isDeleting,
    refetch 
  } = useApplicationUser();

  // Handle form submit
  const handleSubmit = (formData: UserDto) => {
    const userData: UserDto = {
      ...formData,
      schoolId: user?.schoolId || 0,
    };

    if (isEdit) {
      updateUser(userData, {
        onSuccess: () => {
          setModalOpen(false);
          setCurrentUser(undefined);
          setIsEdit(false);
        }
      });
    } else {
      createUser(userData, {
        onSuccess: () => {
          setModalOpen(false);
          setCurrentUser(undefined);
          setIsEdit(false);
        }
      });
    }
  };

  // Handle open add modal
  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentUser(undefined);
    setModalOpen(true);
  };

  // Handle open edit modal
  const handleOpenEditModal = (userData: UserDto) => {
    setIsEdit(true);
    setCurrentUser(userData);
    setModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentUser(undefined);
    setIsEdit(false);
  };

  // Handle delete user
  const handleDeleteUser = () => {
    if (userToDelete && selectedRow?.email) {
      deleteUser(selectedRow.email, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
          setSelectedRow(null);
        }
      });
    }
  };

  // Define columns for EnhancedTable
  const columns = useMemo(() => [
    {
      id: 'username',
      label: t('username'),
      align: 'left' as const,
    },
    {
      id: 'email',
      label: t('email'),
      align: 'left' as const,
    },
    {
      id: 'status',
      label: t('status'),
      align: 'center' as const,
      format: (value: boolean) => (
        <Chip
          label={value ? t("active") : t("inactive")}
          size="small"
          color={value ? "success" : "error"}
        />
      ),
    },
  ], [t]);

  // Loading gate
  if (tenantLoading || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}>
              {t("application_user_management")}
            </Typography>
            {isFetching && <CircularProgress size={16} />}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              size="small"
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
            />
            {isSchoolAdmin() && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                disabled={isCreating || isUpdating || isDeleting}
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                onClick={handleOpenAddModal}
              >
                {isCreating ? t("creating") : t("create_application_user")}
              </Button>
            )}
          </Box>
        </Box>

        <EnhancedTable
          columns={columns}
          data={applicationUsers}
          searchFields={['username', 'email']}
          hideSearch
          externalSearchTerm={searchTerm}
          actions={isSchoolAdmin() ? (row) => (
            <IconButton
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setSelectedRow(row);
              }}
              disabled={isCreating || isUpdating || isDeleting}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          ) : undefined}
          cardProps={{
            elevation: 0,
            sx: {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              mb: 0,
              '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } },
            }
          }}
        />
      </Paper>

      {/* Application User Modal */}
      <ApplicationUserModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentUser}
        onSubmit={handleSubmit}
        isEdit={isEdit}
      />

      {/* Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            if (selectedRow) {
              handleOpenEditModal(selectedRow);
            }
            setAnchorEl(null);
          }}
          disabled={isUpdating || isCreating || isDeleting}
        >
          {isUpdating ? t("updating") : t("edit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedRow?.id) {
              setUserToDelete(selectedRow.id);
              setDeleteDialogOpen(true);
            }
            setAnchorEl(null);
          }}
          disabled={isUpdating || isCreating || isDeleting}
          sx={{ color: "error.main" }}
        >
          {isDeleting ? t("deleting") : t("delete")}
        </MenuItem>
      </Menu>

      {/* Delete Dialog */}
      <CommonModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t("confirm_deletion")}
        maxWidth="xs"
        customActions={
          <>
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setUserToDelete(null);
                setSelectedRow(null);
              }}
              variant="outlined"
              disabled={isDeleting}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleDeleteUser}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {t("deleting")}
                </Box>
              ) : (
                t("delete")
              )}
            </Button>
          </>
        }
      >
        <Typography color="text.secondary">
          {t("are_you_sure_you_want_to_delete_this_user")}
        </Typography>
      </CommonModal>
    </>
  );
}

